import { z } from 'zod'

/**
 * Unified search schema for the PinDetails overlay (Card 4.1).
 *
 * PinDetails (`src/containers/pin-details/router.jsx`) is mounted by 4 bases
 * that resolve to 3 TanStack layout boundaries:
 *
 *   - `pins/$viewType/route.tsx`            (covers /pins noPlan + withPlan)
 *   - `groups/$groupId/$viewType/route.tsx` (group viewer)
 *   - `$planId/_viewer/route.tsx`           (legacy plan-root viewer)
 *
 * This schema is NOT validated directly by a route — it is composed
 * (`.merge()`/`.extend()`) into the boundary schemas (`pinsSearchSchema`,
 * `groupDetailSearchSchema`) so every leaf under those boundaries inherits the
 * overlay params. PinDetails reads them via `useSearch({ strict: false })`,
 * agnostic to which base mounted it.
 *
 * Field ↔ legacy-route map (per the Card 3.1 audit; dead routes dropped):
 *
 *   modal       — the 9 modal gates (Card 4.3: /pinClosedWarning, /title,
 *                 /status, /category, /linkedRecords,
 *                 /showAllTasks, /history, /allVisuals).
 *                 Later 4.x cards may extend PIN_DETAILS_MODALS as they land.
 *                 (`/filter` was dropped — the PO confirmed the pin-list filter
 *                 is a removed feature; see Card 4.4.)
 *   add, sketch — `/:add?/:sketch?` pin-entry positional params (Card 4.2);
 *                 `sketch` is also the post-view tail `/post/:postId/:comment?/
 *                 :sketch?` (Card 4.6).
 *   timePicker  — `/filter/:timePicker?` discriminator (Card 4.4).
 *   postNew     — `/post/new/<kind>/:option?` sub-page (Card 4.5). Live kinds:
 *   postNew     — `/post/new/<kind>/:option?` sub-page (Card 4.5). Live kinds
 *                 only: visuals, whiteSketch, audio (after the Audio→audio
 *                 case fix; note & Documents are dead).
 *   postId      — post view/edit/gallery identity (Cards 4.6–4.8, 4.14).
 *   taskPostId  — task that owns the post being edited; set by
 *                 `CreateVisualAndNote` when opening Visuals from a task
 *                 (Card 4.5). Must stay here so the param survives when
 *                 `.passthrough()` is eventually removed from boundaries.
 *   comment     — `/post/:postId/:comment?` tail (Card 4.6).
 *   taskId      — `/task/task-details/:taskId/...` (Card 4.11).
 *   datasetId,
 *   recordId    — linked-record flows `/addLinkedRecord/:datasetId?/:recordId?`
 *                 and `/linkedRecord/:recordId/:datasetId?` (Cards 4.12/4.13).
 *   fileId      — gallery + sketch targets `/gallery/:postId/:fileId?`,
 *                 `/allVisuals/sketchOn/:postId/:fileId`, full task-details
 *                 form (Cards 4.9, 4.11, 4.14).
 *   commentId   — full task-details form `/task/task-details/:taskId/:option/
 *                 :fileId/:commentId` (Card 4.11).
 *   option,
 *   subOption,
 *   id          — generic legacy positional tails (taskEdit/visualsEdit
 *                 options, task-details short form `:option?/:id?`). Same
 *                 names/types as the viewer schemas, so composition is a no-op
 *                 overlap.
 *
 * NOT here: react-router `location.state` hand-offs (`isReassign`,
 * `isShowAllTask`, ...) — those stay `navigate({ state })` (Card 4.16), never
 * search params. Path identity (`viewType`, `planId`, `pinId`) also stays in
 * the path. (`previousUrl`/`showEdit`/`editUrl`/`status` were dropped entirely:
 * the gallery closes via history `back()` and derives the pin path / editor
 * target from route params — see `Gallery/index.jsx`.)
 */

export const pinDetailsModalSchema = z.enum([
  'pinClosedWarning',
  'title',
  'showAllTasks',
  'history',
  'allVisuals',
  // Card 4.13 — `/addLinkedRecord/:datasetId?/:recordId?` (+ `/all` variants)
  'addLinkedRecord'
])

// Tuple of the modal names — for composing unions in the boundary schemas
// (e.g. groups adds its own `addToReport`/`moveToPlan` values).
export const PIN_DETAILS_MODALS = pinDetailsModalSchema.options

export const pinDetailsSearchSchema = z.object({
  modal: pinDetailsModalSchema.optional(),
  // pin entry (Card 4.2)
  add: z.boolean().optional(),
  sketch: z.boolean().optional(),
  // posts (Cards 4.5–4.8)
  postNew: z.enum(['visuals', 'whiteSketch', 'audio', 'documents']).optional(),
  postId: z.string().optional(),
  taskPostId: z.string().optional(),
  comment: z.string().optional(),
  // post edit kind (Card 4.7): discriminates which edit overlay to open.
  // Present together with postId; 'basic' = note/audio/docs/task posts,
  // 'visuals' = Visuals. Replaces the 6 legacy paths:
  //   /post/:postId/edit, /:postId/edit, /post/:postId/visualsEdit/:option?,
  //   /:postId/visualsEdit/:option?, /post/:postId/taskEdit/:option?/:subOption?,
  //   /:postId/taskEdit/:option?/:subOption?
  editKind: z.enum(['basic', 'visuals']).optional(),
  // tasks (Card 4.11)
  taskId: z.string().optional(),
  // linked records (Cards 4.12/4.13)
  datasetId: z.string().optional(),
  recordId: z.string().optional(),
  // Card 4.13 — "show entire dataset" view inside the addLinkedRecord flow.
  // Replaces the `/addLinkedRecord/:datasetId[/:recordId]/all` positional tail:
  // when set with `modal === 'addLinkedRecord'`, render the full <Dataset> table
  // instead of the record picker.
  allMode: z.boolean().optional(),
  // gallery / media targets (Cards 4.9, 4.11, 4.14)
  fileId: z.string().optional(),
  commentId: z.string().optional(),
  // generic legacy positional tails
  option: z.string().optional(),
  subOption: z.string().optional(),
  id: z.string().optional()
})
